/**
 * AdminDashboardPage — CRM 2.0 Admin-Dashboard
 *
 * Aggregiert alle Karten aus dem System in einem konfigurierbaren Grid.
 * Jede Karte ist:
 *  - Verschiebbar + skalierbar via react-grid-layout
 *  - Aktivierbar/deaktivierbar per Settings-Panel
 *  - In zwei Modi darstellbar: "Übersicht" (summary) oder "ganzes Element" (full)
 *  - Der Titel verlinkt auf die jeweilige Vollliste (Punkt 2)
 *  - Hat einen +-Button für "Neuer Eintrag" (Punkt 3)
 *
 * Layout + Widget-Settings werden per User in dashboard_configs gespeichert.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridLayout, useContainerWidth, type Layout, type LayoutItem, type GridLayoutProps } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import i18n from '../../../i18n';
import { DENSITY_VALUES, getThemeColors } from '../../../lib/theme';
import { useIsMobile } from '../../../lib/useBreakpoint';
import { useDashboardConfig } from '../../../lib/useDashboardConfig';
import {
  v1Dashboard,
  v2Phases,
  v2Dashboard,
  type V1Care,
} from '../../../lib/v1';
import { v2CalendarEvents } from '../../../lib/calendarEvents';
import type { Density } from '../../../types';
import type { DashboardLayoutItem } from '../../../lib/api';
import type { DashboardData } from './types';
import { WIDGETS, effectiveDashboardLayout } from './helpers';
import { DashboardToolbar } from './DashboardToolbar';
import { SettingsPanel } from './SettingsPanel';
import { WidgetCard } from './WidgetCard';
import { ErrorBanner } from './ErrorBanner';

interface AdminDashboardPageProps {
  dark: boolean;
  density: Density;
  setPage: (page: string) => void;
}

export function AdminDashboardPage({ dark, density, setPage }: AdminDashboardPageProps) {
  const t = getThemeColors(dark);
  const d = DENSITY_VALUES[density];
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();

  const {
    config, loading: configLoading, setLayout, setWidgetSettings, widgetSettings,
    source, canSetDefault, resetToDefault, saveAsDefault, clearDefault,
  } = useDashboardConfig(effectiveDashboardLayout);
  const areaControls = { source, canSetDefault, resetToDefault, saveAsDefault, clearDefault };
  const { width: gridWidth, containerRef: gridContainerRef, mounted: gridMounted } = useContainerWidth({ measureBeforeMount: true, initialWidth: 1240 });

  const [data, setData] = useState<DashboardData | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      // allSettled statt all: ein einzelner langsamer oder fehlschlagender
      // Endpunkt darf nicht das GANZE Dashboard leeren. Genau das passierte am
      // 18.09.2026 — die Anrufliste brauchte ~21 s, lief in den 15-s-Timeout
      // des axios-Clients, das Promise.all rejectete und alle sieben Widgets
      // blieben dauerhaft auf "Wird geladen…". Jetzt zeigt jedes Widget, was
      // es hat; nur die kaputten Teile bleiben leer.
      const [badges, newCaresPag, todayEvents, phaseCounts, callList] =
        await Promise.allSettled([
          v1Dashboard.badges(),
          v1Dashboard.newCares({ perPage: 5 }),
          v2CalendarEvents.list(todayStart.toISOString(), todayEnd.toISOString()),
          v2Phases.counts(),
          // Batch-13 — VBS-Dashboard: Anrufliste.
          v2Dashboard.callList({ per_page: 10 }),
        ]);
      if (!alive) return;

      const val = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
        r.status === 'fulfilled' ? r.value : fallback;

      setData({
        badges: val(badges, null as any),
        newCares: (val(newCaresPag, { data: [] } as any).data ?? []) as V1Care[],
        todayEvents: val(todayEvents, [] as any),
        phaseCounts: val(phaseCounts, null as any),
        callList: val(callList, { data: [] } as any).data ?? [],
      });

      // Nur melden, WELCHE Teile fehlen — nicht das ganze Dashboard abwürgen.
      // i18n.t() statt tr() aus dem Hook: so braucht der Effect `tr` nicht als
      // Dependency (exhaustive-deps) und läuft weiterhin nur einmal beim Mount.
      const namen = [
        i18n.t('dashboard.page.section.kpi'),
        i18n.t('betreuung.detail.section.assignments'),
        i18n.t('dashboard.page.section.termine'),
        i18n.t('dashboard.widget.pipeline.title'),
        i18n.t('dashboard.widget.call_list.title'),
      ];
      const fehl = [badges, newCaresPag, todayEvents, phaseCounts, callList]
        .map((r, i) => (r.status === 'rejected' ? namen[i] : null))
        .filter(Boolean);
      setDataError(fehl.length > 0 ? i18n.t('dashboard.page.load_error', { items: fehl.join(', ') }) : null);
    })();
    return () => { alive = false; };
  }, []);

  // Effective layout: stored config layout merged with defaults for any missing widget
  // (entfernte/unbekannte Widget-IDs aus alten Konfigurationen fallen heraus).
  const effectiveLayout: DashboardLayoutItem[] = useMemo(
    () => effectiveDashboardLayout(config.layout),
    [config.layout],
  );

  const handleLayoutChange = useCallback((newLayout: Layout) => {
    const items: DashboardLayoutItem[] = (newLayout as unknown as LayoutItem[]).map(l => ({
      i: l.i, x: l.x, y: l.y, w: l.w, h: l.h,
    }));
    setLayout(items);
  }, [setLayout]);

  const visibleWidgets = WIDGETS.filter(w => widgetSettings(w.id).visible);

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
        <DashboardToolbar
          dark={dark}
          settingsOpen={settingsOpen}
          onToggleSettings={() => setSettingsOpen(v => !v)}
        />
        {settingsOpen && (
          <SettingsPanel
            dark={dark}
            widgets={WIDGETS}
            widgetSettings={widgetSettings}
            setWidgetSettings={setWidgetSettings}
            areaControls={areaControls}
          />
        )}
        {dataError && <ErrorBanner dark={dark} message={dataError} />}
        {visibleWidgets.map(w => (
          <WidgetCard
            key={w.id}
            widget={w}
            mode={widgetSettings(w.id).mode}
            dark={dark}
            density={density}
            data={data}
            setPage={setPage}
            tr={tr}
          />
        ))}
      </div>
    );
  }

  // Desktop: react-grid-layout v2 API — width tracks the actual container
  // (ResizeObserver) instead of a fixed 1240px so the grid stays responsive
  // across window sizes / tablet widths.
  const gap = typeof d.gap === 'number' ? d.gap : 12;

  const rglLayout: LayoutItem[] = effectiveLayout
    .filter(l => widgetSettings(l.i).visible)
    .map(l => {
      const def = WIDGETS.find(w => w.id === l.i);
      return {
        i: l.i, x: l.x, y: l.y, w: l.w, h: l.h,
        minW: def?.minW ?? 2, minH: def?.minH ?? 2,
      };
    });

  const gridConfig: GridLayoutProps['gridConfig'] = {
    cols: 12,
    rowHeight: 60,
    margin: [gap, gap] as const,
    containerPadding: [0, 0] as const,
  };

  const dragConfig: GridLayoutProps['dragConfig'] = {
    enabled: true,
    handle: '.widget-drag-handle',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <DashboardToolbar
        dark={dark}
        settingsOpen={settingsOpen}
        onToggleSettings={() => setSettingsOpen(v => !v)}
      />

      {settingsOpen && (
        <SettingsPanel
          dark={dark}
          widgets={WIDGETS}
          widgetSettings={widgetSettings}
          setWidgetSettings={setWidgetSettings}
          areaControls={areaControls}
        />
      )}

      {dataError && <ErrorBanner dark={dark} message={dataError} />}

      <div ref={gridContainerRef as React.RefObject<HTMLDivElement>} style={{ width: '100%' }}>
      {(configLoading || !gridMounted) ? (
        <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('dashboard.page.loading_config')}</div>
      ) : (
        <GridLayout
          layout={rglLayout as unknown as Layout}
          width={gridWidth}
          gridConfig={gridConfig}
          dragConfig={dragConfig}
          onLayoutChange={handleLayoutChange}
          className="admin-dashboard-grid"
          style={{ minHeight: 400 }}
        >
          {visibleWidgets.map(w => (
            <div key={w.id} style={{ overflow: 'hidden' }}>
              <WidgetCard
                widget={w}
                mode={widgetSettings(w.id).mode}
                dark={dark}
                density={density}
                data={data}
                setPage={setPage}
                tr={tr}
                draggable
              />
            </div>
          ))}
        </GridLayout>
      )}
      </div>
    </div>
  );
}
